class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindDrawerEvents();
    this.setHeaderCartIconAccessibility();
  }

  bindDrawerEvents() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.myugenBound === 'true') return;

    overlay.dataset.myugenBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-open], a[href="/cart"], a[href$="/cart"], #cart-icon-bubble, .shop-cart-link, [data-route="bag"]');
      if (!trigger) return;

      const href = trigger.getAttribute('href');
      const isCartRoute = href && new URL(href, window.location.origin).pathname === '/cart';
      const isBagRoute = trigger.getAttribute('data-route') === 'bag' || href === '#bag';
      if (!trigger.hasAttribute('data-cart-open') && !isCartRoute && !isBagRoute && trigger.id !== 'cart-icon-bubble') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(trigger);
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.addFromForm(form);
    }, true);
  }

  addFromForm(form) {
    const submit = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    const sections = this.getSectionsToRender().map((section) => section.id);

    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);
    submit?.setAttribute('disabled', 'disabled');

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) throw parsedState;
        if (parsedState.sections && parsedState.sections['cart-drawer']) {
          this.renderContents(parsedState);
          return;
        }

        return this.refreshDrawerSection().then(() => this.open(submit));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        submit?.removeAttribute('disabled');
      });
  }

  refreshDrawerSection() {
    return fetch(`${(window.routes && window.routes.cart_url) || '/cart'}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = html.querySelector('cart-drawer');

        if (!sourceDrawer) throw new Error('Cart drawer section did not render.');

        this.innerHTML = sourceDrawer.innerHTML;
        this.className = sourceDrawer.className;
        this.bindDrawerEvents();
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const sourceDrawer = this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer');

      if (sourceDrawer) {
        this.innerHTML = sourceDrawer.innerHTML;
        this.className = sourceDrawer.className;
        this.bindDrawerEvents();
      }
    }

    const bubble = parsedState.sections && parsedState.sections['cart-icon-bubble'];
    const bubbleTarget = bubble && document.getElementById('cart-icon-bubble');
    const bubbleSource = bubble && this.getSectionDOM(bubble, '.shopify-section');
    if (bubbleTarget && bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;

    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.myugenCartDrawer = {
  open() {
    document.querySelector('cart-drawer')?.open();
  },
  close() {
    document.querySelector('cart-drawer')?.close();
  }
};

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
